// src/pages/rakuten-event/RakutenEventProductEdit.jsx
import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';

const RakutenEventProductEdit = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const uuid = searchParams.get('uuid'); // 編集時の対象UUID
  const key = searchParams.get('key');   // 新規作成時の基準カテゴリ(dress, bag, bolero)

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

  // 📝 カテゴリの定義
  const categories = {
    'dress': 'パーティードレス',
    'bag': 'パーティーバッグ',
    'bolero': 'ボレロ・ショール'
  };

  // 🛍️ 旧システムの rakutenITEM 状態を完全移植
  const [formData, setFormData] = useState({
    uuid: '',
    url: '',        // 楽天商品URL (呼び出し用)
    link: '',       // リンクURL (実際のリンク先)
    imgURL: '',     // 画像URL
    name: '',       // 商品名
    apiCheck: true, // チェック外した場合APIから取得
    itemCode: '',   // アイテムコード
    price: '',      // 価格
    delPrice: '',   // 消し値
    txtBadge: '',   // 下バッジ
    imgBadge: '',   // 画像上バッジ
    category: key ? [key] : [] // 所属カテゴリ配列
  });

  const [loading, setLoading] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');

  // 🔃 既存データの編集であれば初期データを取得
  useEffect(() => {
    if (uuid) {
      const fetchProductData = async () => {
        try {
          const res = await fetch(`${API_URL}/rakuten-cart-under/product/${uuid}`);
          if (res.ok) {
            const data = await res.json();
            const p = data.data;
            setFormData({
              uuid: p.uuid,
              url: p.item_url || '',
              link: p.item_url || '',
              imgURL: p.item_img || '',
              name: p.item_name || '',
              apiCheck: p.title_check,
              itemCode: p.item_code || '',
              price: p.text_price || '',
              delPrice: p.text_del_price || '',
              txtBadge: p.text_badge || '',
              imgBadge: p.img_badge || '',
              category: p.categories || []
            });
          }
        } catch (error) {
          console.error("商品データの取得失敗:", error);
        }
      };
      fetchProductData();
    }
  }, [uuid, API_URL]);

  // リアルタイムプレビュー用HTMLの自動合成 (旧 myframe ➔ srcDoc 連動の役割)
  useEffect(() => {
    const fallbackImg = "https://www.rakuten.ne.jp/gold/twinklegirls/main/carousel/img/twinkle-girls.png";
    const html = `
      <div style="padding: 20px; background: #fff; display: flex; justify-content: center;">
        <div class="card card-product" style="width: 240px; border: 1px solid #eee; border-radius: 4px; padding: 10px; font-family: sans-serif; position: relative;">
          <a href="${formData.link || '#'}" target="_blank">
            <img src="${formData.imgURL || fallbackImg}" style="width: 100%; height: 200px; object-fit: cover; border-radius: 4px;">
          </a>
          <div style="margin-top: 10px;">
            <div style="font-size: 13px; font-weight: bold; color: #333; height: 36px; overflow: hidden; line-height: 1.4;">
              ${formData.name || '商品名タイトル'}
            </div>
            <div style="margin-top: 8px; color: #e4393c; font-weight: bold; font-size: 15px;">
              ${formData.price ? `¥${formData.price}` : '¥9,999'}
              ${formData.delPrice ? `<del style="color: #999; font-size: 11px; margin-left: 5px;">¥${formData.delPrice}</del>` : ''}
            </div>
            ${formData.txtBadge ? `<span style="background: #e4393c; color: #fff; font-size: 10px; padding: 2px 4px; border-radius: 3px; margin-top: 5px; display: inline-block;">${formData.txtBadge}</span>` : ''}
            ${formData.imgBadge ? `<span style="position: absolute; top: 15px; left: 15px; background: rgba(0,0,0,0.7); color: #fff; font-size: 10px; padding: 2px 6px; border-radius: 10px;">${formData.imgBadge}</span>` : ''}
          </div>
        </div>
      </div>
    `;
    setPreviewHtml(html);
  }, [formData]);

  // 📞 【大注目】楽天商品URL「呼び出し」ボタンの解析アクション（旧 itemCall）
  const handleItemCall = async () => {
    if (!formData.url) {
      alert("楽天商品URLを入力してください。");
      return;
    }
    try {
      setLoading(true);
      // FastAPI側のスクレイピング・APIエンドポイントを叩く
      const res = await fetch(`${API_URL}/rakuten-cart-under/fetch-rakuten`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: formData.url })
      });
      if (res.ok) {
        const result = await res.json();
        const r = result.data;
        // 取得データをフォームに全自動流し込み！
        setFormData(prev => ({
          ...prev,
          link: r.item_url,
          imgURL: r.item_img,
          name: r.item_name,
          itemCode: r.item_code,
          price: r.text_price,
          delPrice: r.text_del_price
        }));
      } else {
        alert("楽天からのデータ取得に失敗しました。URLを確認してください。");
      }
    } catch (error) {
      console.error("呼び出しエラー:", error);
    } finally {
      setLoading(false);
    }
  };

  // カテゴリーのチェックボックス選択制御
  const handleCategoryCheck = (cateKey) => {
    setFormData(prev => {
      const current = [...prev.category];
      if (current.includes(cateKey)) {
        return { ...prev, category: current.filter(c => c !== cateKey) };
      } else {
        return { ...prev, category: [...current, cateKey] };
      }
    });
  };

  // 💾 送信処理 (旧 formSubmit)
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.category.length === 0) {
      alert("少なくとも1つのカテゴリーを選択してください。");
      return;
    }

    try {
      const method = uuid ? 'PUT' : 'POST';
      const url = uuid ? `${API_URL}/rakuten-cart-under/product/${uuid}` : `${API_URL}/rakuten-cart-under/product/`;

      const res = await fetch(url, {
        method: method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        alert("商品を安全に保存しました！");
        navigate('/rakuten-event/products');
      } else {
        alert("保存に失敗しました。");
      }
    } catch (error) {
      console.error("保存エラー:", error);
    }
  };

  return (
    <div>
      <ol className="breadcrumb float-xl-end">
        <li className="breadcrumb-item"><Link to="/">Home</Link></li>
        <li className="breadcrumb-item"><Link to="/rakuten-event/products">カート下商品管理</Link></li>
        <li className="breadcrumb-item active">商品登録・編集</li>
      </ol>
      <h1 className="page-header">下カート商品 登録・編集</h1>

      <div className="row">
        {/* 左側：入力フォーム */}
        <div className="col-xl-7">
          <div className="panel panel-inverse">
            <div className="panel-heading">
              <h4 className="panel-title">商品登録情報</h4>
            </div>
            <div className="panel-body bg-light">
              <form onSubmit={handleSubmit}>
                <legend className="mb-3" style={{ fontSize: '16px', fontWeight: 'bold' }}>商品マスターデータ</legend>

                {/* 楽天URL呼び出し */}
                <div className="form-group row mb-3">
                  <label className="col-md-3 col-form-label fw-bold">商品楽天URL</label>
                  <div className="col-md-9">
                    <div className="input-group">
                      <input type="text" className="form-control" placeholder="https://item.rakuten.co.jp/..." value={formData.url} onChange={e => setFormData({ ...formData, url: e.target.value })} />
                      <button type="button" className="btn btn-primary" onClick={handleItemCall} disabled={loading}>
                        {loading ? <i className="fa fa-spinner fa-spin"></i> : '呼び出し'}
                      </button>
                    </div>
                  </div>
                </div>

                <hr />

                {/* リンクURL */}
                <div className="form-group row mb-3">
                  <label className="col-md-3 col-form-label fw-bold">リンクURL <span className="text-danger">*</span></label>
                  <div className="col-md-9">
                    <input type="text" className="form-control" value={formData.link} onChange={e => setFormData({ ...formData, link: e.target.value })} required />
                  </div>
                </div>

                {/* 画像URL */}
                <div className="form-group row mb-3">
                  <label className="col-md-3 col-form-label fw-bold">画像URL <span className="text-danger">*</span></label>
                  <div className="col-md-9">
                    <input type="text" className="form-control" value={formData.imgURL} onChange={e => setFormData({ ...formData, imgURL: e.target.value })} required />
                  </div>
                </div>

                {/* 商品名 */}
                <div className="form-group row mb-3">
                  <label className="col-md-3 col-form-label fw-bold">商品名 <span class="text-danger">*</span></label>
                  <div className="col-md-9">
                    <input type="text" className="form-control" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required />
                  </div>
                </div>

                {/* APIチェック */}
                <div className="form-group row mb-3">
                  <div className="col-md-9 offset-md-3">
                    <div className="form-check form-switch">
                      <input className="form-check-input" type="checkbox" id="apiCheck" checked={formData.apiCheck} onChange={e => setFormData({ ...formData, apiCheck: e.target.checked })} />
                      <label className="form-check-label text-muted" htmlFor="apiCheck">チェックを外した場合、楽天APIから最新情報を自動取得</label>
                    </div>
                  </div>
                </div>

                {/* アイテムコード */}
                <div className="form-group row mb-3">
                  <label className="col-md-3 col-form-label fw-bold">アイテムコード <span className="text-danger">*</span></label>
                  <div className="col-md-9">
                    <input type="text" className="form-control" placeholder="例: partydress5014" value={formData.itemCode} onChange={e => setFormData({ ...formData, itemCode: e.target.value })} required />
                  </div>
                </div>

                {/* 価格 ＆ 消し値 */}
                <div className="form-group row mb-3">
                  <label className="col-md-3 col-form-label fw-bold">価格 / 消し値</label>
                  <div className="col-md-4">
                    <input type="text" className="form-control" placeholder="価格 (例: 5900)" value={formData.price} onChange={e => setFormData({ ...formData, price: e.target.value })} />
                  </div>
                  <div className="col-md-5">
                    <input type="text" className="form-control" placeholder="消し値定価 (例: 12800)" value={formData.delPrice} onChange={e => setFormData({ ...formData, delPrice: e.target.value })} />
                  </div>
                </div>

                {/* 各種バッジ */}
                <div className="form-group row mb-3">
                  <label className="col-md-3 col-form-label fw-bold">上下バッジ仕様</label>
                  <div className="col-md-4">
                    <input type="text" className="form-control" placeholder="下バッジ (例: 10% OFF)" value={formData.txtBadge} onChange={e => setFormData({ ...formData, txtBadge: e.target.value })} />
                  </div>
                  <div className="col-md-5">
                    <input type="text" className="form-control" placeholder="画像上バッジ (例: 送料無料)" value={formData.imgBadge} onChange={e => setFormData({ ...formData, imgBadge: e.target.value })} />
                  </div>
                </div>

                {/* カテゴリー複数選択 */}
                <div className="form-group row mb-4">
                  <label className="col-md-3 col-form-label fw-bold">所属カテゴリー</label>
                  <div className="col-md-9 d-flex align-items-center gap-3">
                    {Object.entries(categories).map(([cKey, cName]) => (
                      <div className="form-check form-check-inline" key={cKey}>
                        <input className="form-check-input" type="checkbox" id={`check-${cKey}`} checked={formData.category.includes(cKey)} onChange={() => handleCategoryCheck(cKey)} />
                        <label className="form-check-label fw-bold text-dark" htmlFor={`check-${cKey}`}>{cName}</label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* ボタン類 */}
                <div className="form-group row">
                  <div className="col-md-9 offset-md-3">
                    <button type="submit" className="btn btn-primary me-2"><i className="fa fa-save me-1"></i>送信・保存</button>
                    <Link to="/rakuten-event/products" className="btn btn-default">登録せず商品一覧へ戻る</Link>
                  </div>
                </div>

              </form>
            </div>
          </div>
        </div>

        {/* 右側：リアルタイムカードプレビュー */}
        <div className="col-xl-5">
          <div className="panel panel-inverse" style={{ position: 'sticky', top: '20px' }}>
            <div className="panel-heading">
              <h4 className="panel-title">リアルタイム商品カードビュー</h4>
            </div>
            <div className="panel-body p-0 bg-dark-20">
              <iframe srcDoc={previewHtml} width="100%" height="380px" style={{ border: 'none' }} title="Product Card Preview"></iframe>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default RakutenEventProductEdit;